import React, { useState, useCallback, useEffect } from "react";
import { GameBoard } from "./components/GameBoard";
import { Scoreboard } from "./components/Scoreboard";
import { TurnIndicator } from "./components/TurnIndicator";
import { Button } from "./components/ui/button";
import "./index.css";

/* ── Types ── */
type CellValue = "X" | "O" | null;
type Player = "X" | "O";
interface Scores {
  x: number;
  o: number;
  draws: number;
}

/* ── Win lines ── */
const WIN_LINES = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

/* ── localStorage helpers ── */
const STORAGE_KEY = "ttt-duel-scores";

function loadScores(): Scores {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        x: typeof parsed.x === "number" ? parsed.x : 0,
        o: typeof parsed.o === "number" ? parsed.o : 0,
        draws: typeof parsed.draws === "number" ? parsed.draws : 0,
      };
    }
  } catch {
    /* ignore corrupted data */
  }
  return { x: 0, o: 0, draws: 0 };
}

function saveScores(scores: Scores) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scores));
  } catch {
    /* quota exceeded, ignore */
  }
}

/* ── Game logic ── */
function checkWinner(board: CellValue[]): { winner: Player; line: number[] } | null {
  for (const line of WIN_LINES) {
    const [a, b, c] = line;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a] as Player, line };
    }
  }
  return null;
}

function checkDraw(board: CellValue[]): boolean {
  return board.every((cell) => cell !== null);
}

/* ── App ── */
export function App() {
  const [board, setBoard] = useState<CellValue[]>(Array(9).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState<Player>("X");
  const [winner, setWinner] = useState<Player | null>(null);
  const [winningLine, setWinningLine] = useState<number[] | null>(null);
  const [isDraw, setIsDraw] = useState(false);
  const [scores, setScores] = useState<Scores>(loadScores);

  const roundOver = winner !== null || isDraw;

  // Persist scores
  useEffect(() => {
    saveScores(scores);
  }, [scores]);

  const handleCellClick = useCallback(
    (index: number) => {
      if (board[index] !== null || roundOver) return;

      const newBoard = [...board];
      newBoard[index] = currentPlayer;
      setBoard(newBoard);

      const result = checkWinner(newBoard);
      if (result) {
        setWinner(result.winner);
        setWinningLine(result.line);
        setScores((prev) => ({
          ...prev,
          [result.winner.toLowerCase()]: prev[result.winner.toLowerCase() as "x" | "o"] + 1,
        }));
      } else if (checkDraw(newBoard)) {
        setIsDraw(true);
        setScores((prev) => ({ ...prev, draws: prev.draws + 1 }));
      } else {
        setCurrentPlayer(currentPlayer === "X" ? "O" : "X");
      }
    },
    [board, currentPlayer, roundOver]
  );

  const handleNextRound = useCallback(() => {
    setBoard(Array(9).fill(null));
    setWinner(null);
    setWinningLine(null);
    setIsDraw(false);
    // Loser goes first, or alternate on draw
    if (winner) {
      setCurrentPlayer(winner === "X" ? "O" : "X");
    } else {
      setCurrentPlayer((prev) => (prev === "X" ? "O" : "X"));
    }
  }, [winner]);

  const handleResetAll = useCallback(() => {
    setBoard(Array(9).fill(null));
    setWinner(null);
    setWinningLine(null);
    setIsDraw(false);
    setCurrentPlayer("X");
    const empty = { x: 0, o: 0, draws: 0 };
    setScores(empty);
    saveScores(empty);
  }, []);

  return (
    <div className="flex flex-col items-center gap-6 sm:gap-8 px-4 py-8 sm:py-12 w-full max-w-lg mx-auto select-none">
      {/* Title */}
      <h1
        className="fade-up text-2xl sm:text-3xl font-extrabold tracking-tight text-center"
        style={{
          opacity: 0,
          fontFamily: "var(--font-display)",
          background: "linear-gradient(135deg, var(--color-x) 0%, var(--color-o) 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        Tic-Tac-Toe Duel
      </h1>

      {/* Scoreboard */}
      <Scoreboard scores={scores} />

      {/* Turn / Result */}
      <TurnIndicator
        currentPlayer={currentPlayer}
        winner={winner}
        isDraw={isDraw}
      />

      {/* Board */}
      <GameBoard
        board={board}
        onCellClick={handleCellClick}
        winningLine={winningLine}
        disabled={roundOver}
      />

      {/* Action Buttons */}
      <div
        className="fade-up delay-3 flex items-center gap-3"
        style={{ opacity: 0 }}
      >
        {roundOver && (
          <Button
            onClick={handleNextRound}
            className="result-enter px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200"
            style={{
              fontFamily: "var(--font-display)",
              background:
                winner === "O"
                  ? "var(--color-o)"
                  : winner === "X"
                    ? "var(--color-x)"
                    : "var(--color-dim)",
              color: "#0a0e17",
            }}
          >
            Next Round
          </Button>
        )}
        <Button
          onClick={handleResetAll}
          variant="outline"
          className="px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200"
          style={{
            fontFamily: "var(--font-display)",
            borderColor: "var(--color-grid-line)",
            color: "var(--color-dim)",
          }}
        >
          Reset All
        </Button>
      </div>

      {/* Footer hint */}
      <p
        className="fade-up delay-5 text-[11px] tracking-widest uppercase"
        style={{
          opacity: 0,
          color: "var(--color-dim)",
          fontFamily: "var(--font-mono)",
        }}
      >
        Pass &amp; Play · Two Players
      </p>
    </div>
  );
}

export default App;
